import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType, SwiperOptions } from 'swiper/types';
import { A11y, Pagination, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { Paragraph } from 'components/src/typography';
import { Maybe } from 'types/generated';
import { useState } from 'react';
import clsx from 'clsx';

const DEFAULT_BREAKPOINTS = {
  544: {
    slidesPerView: 2,
    spaceBetween: 16,
  },
  768: {
    slidesPerView: 2.5,
    spaceBetween: 20,
  },
  1024: {
    slidesPerView: 3.5,
    spaceBetween: 24,
  },
  1280: {
    slidesPerView: 3.5,
    spaceBetween: 24,
  },
};

type Props = {
  cards: Array<React.ReactNode>;
  disclaimer?: Maybe<string>;
  breakpoints?: {
    [width: number]: SwiperOptions;
  };
  showShadows?: boolean;
  backgroundColor?: string;
  layout?: string;
  isMobile?: boolean;
};

const SynCardCarousel = ({
  cards,
  breakpoints = DEFAULT_BREAKPOINTS,
  disclaimer,
  showShadows = false,
  backgroundColor = 'white-15',
  layout = 'default',
  isMobile = false,
}: Props) => {
  const [showRightShadow, setShowRightShadow] = useState(false);
  const [showLeftShadow, setShowLeftShadow] = useState(true);

  const handleSetShadow = (e: SwiperType) => {
    setShowLeftShadow(e.pagination.bullets.length > 1);
    setShowRightShadow(!e.isEnd && e.pagination.bullets.length > 1);
  };
  // from-white-15 from-red-100 from-black-full from-white-100 from-transparent
  return (
    <div className="relative col-span-full -ml-24">
      {showShadows && !isMobile && showLeftShadow && (
        <div
          className={`absolute left-0 top-0 z-10 h-full w-24 bg-gradient-to-r from-${backgroundColor} to-transparent`}
        />
      )}
      {showShadows && !isMobile && showRightShadow && (
        <div
          className={`absolute right-0 top-0 z-10 h-full w-24 bg-gradient-to-l from-${backgroundColor} to-transparent`}
        />
      )}
      <Swiper
        modules={[Pagination, A11y, Mousewheel]}
        pagination={{ clickable: true }}
        mousewheel={{ forceToAxis: true }}
        spaceBetween={12}
        slidesPerView={1.2}
        breakpoints={breakpoints}
        className="!flex !flex-col !pl-24 !pr-12"
        onSlideChange={handleSetShadow}
        onInit={handleSetShadow}
        onResize={handleSetShadow}
      >
        {cards.map((card, index) => (
          <SwiperSlide
            style={{
              display: 'flex',
              height: 'auto',
              boxShadow: isMobile
                ? 'none'
                : `-6px 40px 30px rgba(56, 4, 10, 0.13),
                -3px 25px 20px rgba(56, 4, 10, 0.21),
                -1px 10px 12px rgba(56, 4, 10, 0.25)`,
            }}
            className={clsx(`responsive-slide my-64 mt-16 rounded-28`, {
              'four-box-responsive-slide': layout === 'four_box_horizontal_scroll',
              'mr-12': index === cards.length - 1,
            })}
            key={index}
          >
            {card}
          </SwiperSlide>
        ))}

        {disclaimer && (
          <div className={`rounded-28 ${backgroundColor} p-8 pt-0 lg:p-16 lg:pt-0`}>
            <Paragraph classes="inline text-red-100" variant="small">
              {'*'}
            </Paragraph>
            <Paragraph variant="small" classes="inline text-black-100">
              {disclaimer}
            </Paragraph>
          </div>
        )}
      </Swiper>
    </div>
  );
};

export default SynCardCarousel;
